import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Building2, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import { useIndustryBenchmarks, useCompanyProfile, updateCompanyProfile } from "@/hooks/useVendorIntelligence";
import { useState } from "react";
import { toast } from "sonner";

interface IndustryBenchmarkCardProps {
  userSpending?: Record<string, number>; // category -> amount
}

const SIZE_OPTIONS = [
  { value: "micro", label: "Micro (<₹25L revenue)" },
  { value: "small", label: "Small (₹25L-5Cr)" },
  { value: "medium", label: "Medium (₹5-50Cr)" },
  { value: "large", label: "Large (>₹50Cr)" },
];

const INDUSTRY_OPTIONS = [
  { value: "manufacturing", label: "Manufacturing" },
  { value: "services", label: "Services" },
  { value: "retail", label: "Retail" },
  { value: "technology", label: "Technology" },
  { value: "logistics", label: "Logistics" },
  { value: "healthcare", label: "Healthcare" },
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

export function IndustryBenchmarkCard({ userSpending = {} }: IndustryBenchmarkCardProps) {
  const { data: profile, refetch: refetchProfile } = useCompanyProfile();
  const [selectedSize, setSelectedSize] = useState<string>(profile?.company_size_bucket || "small");
  const [selectedIndustry, setSelectedIndustry] = useState<string>(profile?.industry_category || "");
  const [isEditing, setIsEditing] = useState(!profile);

  const { data: benchmarks, isLoading } = useIndustryBenchmarks(selectedSize, selectedIndustry || undefined);

  const handleSaveProfile = async () => {
    try {
      await updateCompanyProfile({
        company_size_bucket: selectedSize,
        industry_category: selectedIndustry || undefined,
      });
      await refetchProfile();
      setIsEditing(false);
      toast.success("Company profile updated");
    } catch (error) {
      toast.error("Failed to save profile");
    }
  };

  return (
    <Card className="p-6 border-border/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-bold text-foreground">Industry Benchmarks</h3>
        </div>
        {!isEditing && (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
            Change segment
          </Button>
        )}
      </div>

      {/* Profile Selection */}
      {isEditing && (
        <div className="mb-4 p-4 rounded-lg bg-muted/50 border border-border/50">
          <p className="text-sm text-muted-foreground mb-3">
            Select your company profile to see relevant benchmarks:
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-muted-foreground">Company Size</label>
              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SIZE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Industry</label>
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger>
                  <SelectValue placeholder="Any industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Any industry</SelectItem>
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button size="sm" onClick={handleSaveProfile}>
            Save & View Benchmarks
          </Button>
        </div>
      )}

      {/* Benchmarks Display */}
      {isLoading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
        </div>
      ) : !benchmarks || benchmarks.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            Not enough data for your segment yet. As more companies your size use Sentinel, benchmarks will appear.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">
              {SIZE_OPTIONS.find(o => o.value === selectedSize)?.label}
            </Badge>
            {selectedIndustry && (
              <Badge variant="outline" className="text-xs">
                {INDUSTRY_OPTIONS.find(o => o.value === selectedIndustry)?.label}
              </Badge>
            )}
          </div>

          {benchmarks.slice(0, 5).map((benchmark) => {
            const userAmount = userSpending[benchmark.spending_category] || 0;
            const comparison = userAmount > 0 
              ? ((userAmount - benchmark.median_monthly_spend) / benchmark.median_monthly_spend) * 100
              : null;

            return (
              <div
                key={benchmark.spending_category}
                className="p-3 rounded-lg bg-surface hover:bg-surface/80 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium capitalize text-foreground">
                      {benchmark.spending_category.replace(/_/g, " ")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Typical: {formatCurrency(benchmark.median_monthly_spend)}/mo
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      Range: {formatCurrency(benchmark.percentile_25)} - {formatCurrency(benchmark.percentile_75)}
                    </p>
                    {comparison !== null && (
                      <div className={`flex items-center justify-end gap-1 text-xs ${
                        comparison > 20 ? "text-warning" : comparison < -20 ? "text-success" : "text-muted-foreground"
                      }`}>
                        {comparison > 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        You: {comparison > 0 ? "+" : ""}{comparison.toFixed(0)}%
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Visual bar */}
                <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div 
                      className="bg-muted-foreground/30 h-full"
                      style={{ width: `${(benchmark.percentile_25 / benchmark.percentile_75) * 100}%` }}
                    />
                    <div 
                      className="bg-primary h-full"
                      style={{ 
                        width: `${((benchmark.median_monthly_spend - benchmark.percentile_25) / benchmark.percentile_75) * 100}%` 
                      }}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mt-1">
                  Based on {benchmark.sample_count} similar companies
                </p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
